import React, { useEffect, useRef, useState } from "react";

import { cn } from "../../lib/utils";

/**
 * Retailer Assistant side panel (Compass "Your Assistant").
 *
 * The PDP chat surface that opens when a retailer taps the assistant: a header
 * with the Faire mark + close, a scrollable thread (AI disclaimer, product
 * context card, and user/assistant messages — research collapsible, markdown
 * answers with paragraphs/lists, thumbs feedback, follow-up suggestion pills),
 * and a composer pinned to the bottom.
 *
 * Faithfully ported from production markup. Ported as a full-viewport-height
 * left rail (its dock position on the PDP).
 */

type AssistantSegment =
  | { kind: "paragraph"; content: React.ReactNode }
  | { kind: "list"; items: React.ReactNode[] };

interface UserMessage {
  role: "user";
  text: string;
}

interface AssistantMessage {
  role: "assistant";
  /** Show the collapsed "Researched" disclosure above the answer. */
  researched?: boolean;
  segments: AssistantSegment[];
  /** Follow-up suggestion pills shown beneath the answer. */
  followUps?: string[];
}

export type AssistantThreadMessage = UserMessage | AssistantMessage;

export interface RetailerAssistantPanelProps {
  productName?: string;
  productBrand?: string;
  productImage?: string;
  messages?: AssistantThreadMessage[];
  /** Show the "Researching…" loading bubble while an answer streams in. */
  pending?: boolean;
  /** Close button handler. When omitted the close button is inert. */
  onClose?: () => void;
  /** Composer submit handler. When provided the composer becomes interactive. */
  onSend?: (text: string) => void;
  /** Bump to refocus the composer (e.g. when the panel opens empty). */
  autoFocusKey?: number;
}

const DEFAULT_MESSAGES: AssistantThreadMessage[] = [
  { role: "user", text: "What's the margin on this product?" },
  {
    role: "assistant",
    researched: true,
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            The gross margin at the suggested retail price is{" "}
            <strong>50%</strong>.
          </>
        ),
      },
      {
        kind: "list",
        items: [
          <>
            <strong>Wholesale: $19.50</strong>
          </>,
          <>
            <strong>Suggested retail: $39.00</strong>
          </>,
          <>
            <strong>Gross margin: 50%</strong>
          </>,
        ],
      },
    ],
  },
  { role: "user", text: "How long until it ships?" },
  {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            Based on the brand&rsquo;s actual shipment history, it typically
            ships in <strong>4 to 7 days</strong>. The brand&rsquo;s
            self-reported lead time is <strong>2 days</strong>, but the observed
            range is the better guide for planning.
          </>
        ),
      },
      {
        kind: "paragraph",
        content: (
          <>
            If you&rsquo;re asking about arrival rather than ship-out, the
            current estimated delivery is <strong>Jun 23 - Jul 1</strong>.
          </>
        ),
      },
    ],
  },
  { role: "user", text: "What's the margin on this product?" },
  {
    role: "assistant",
    segments: [
      {
        kind: "paragraph",
        content: (
          <>
            The gross margin on this product is <strong>50%</strong> at the
            suggested retail price.
          </>
        ),
      },
      {
        kind: "list",
        items: [
          <>
            <strong>Wholesale: $19.50</strong>
          </>,
          <>
            <strong>Suggested retail: $39.00</strong>
          </>,
          <>
            <strong>Gross margin: 50%</strong>
          </>,
        ],
      },
    ],
    followUps: [
      "What's the brand minimum?",
      "How long until it arrives?",
      "Is it sold in pre-packs?",
    ],
  },
];

const FOLLOW_UP_PILL_CLASS =
  "focusable interactive type-fs-paragraph border-fs-action-border-subdued text-fs-action-text-default box-border flex h-8 max-w-full min-w-0 items-center justify-start overflow-hidden whitespace-nowrap rounded-full border bg-transparent px-3 hover:border-fs-action-border-hover hover:text-fs-action-text-hover";

const RetailerAssistantPanel: React.FC<RetailerAssistantPanelProps> = ({
  productName = "Earth Petrichor 8 oz. Candle",
  productBrand = "Night Blooms Goods",
  productImage = "https://cdn.faire.com/fastly/a702bc726e1fd3ca6d1f50de377c2ec912ca4d499fb67b2ba8ff7dede7005fac.png",
  messages = DEFAULT_MESSAGES,
  pending = false,
  onClose,
  onSend,
  autoFocusKey = 0,
}) => {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const interactive = Boolean(onSend);

  // Keep the latest message / loading bubble in view as the thread grows.
  useEffect(() => {
    if (interactive) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, pending, interactive]);

  // Focus the composer when the panel opens without a seeded question.
  useEffect(() => {
    if (autoFocusKey > 0) {
      textareaRef.current?.focus();
    }
  }, [autoFocusKey]);

  const submitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed || !onSend) {
      return;
    }
    onSend(trimmed);
    setDraft("");
  };

  return (
    <div className="super-assistant-side-panel border-fs-action-border-subdued bg-fs-surface-secondary flex h-full min-h-0 w-full min-w-0 flex-col border-r shadow-sm">
      {/* Header */}
      <div className="border-fs-action-border-subdued flex shrink-0 items-center justify-between gap-2 border-b px-5 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="bg-(--slate-color-grey-900) flex h-5 w-5 shrink-0 items-center justify-center rounded">
            <FaireMark />
          </div>
          <p className="type-fs-paragraph-medium text-fs-text-primary min-w-0">
            Your Assistant
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="hover:bg-fs-action-surface-inverse-hover rounded-[6px] bg-transparent px-[6px] py-[4px] transition-colors"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Scrollable thread (with bottom fade) */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          aria-hidden="true"
          className="from-fs-surface-secondary pointer-events-none absolute right-4 bottom-0 left-0 z-10 h-6 bg-gradient-to-t to-transparent"
        />
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto pt-0 pr-2 pb-6 pl-5">
          <span className="text-fs-text-subdued flex items-center gap-1 pt-2 pb-1 text-[12px] font-[200]">
          This is AI-generated content. It&rsquo;s meant to be helpful, but
          isn&rsquo;t always perfect and may contain errors.
        </span>

        {/* Product context card */}
        <div
          className="bg-fs-surface-primary flex w-full min-w-0 shrink-0 gap-3 rounded-lg px-4 py-3"
          data-test-id="pdpAssistantProductCard"
        >
          <div className="size-9 shrink-0 overflow-hidden rounded">
            <img
              alt=""
              aria-hidden="true"
              className="size-full object-cover"
              src={productImage}
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 overflow-hidden">
            <p className="type-fs-paragraph-medium text-fs-text-primary truncate">
              {productName}
            </p>
            <p className="type-fs-label text-fs-text-subdued truncate">
              {productBrand}
            </p>
          </div>
        </div>

        {messages.map((message, index) =>
          message.role === "user" ? (
            <UserBubble key={index} text={message.text} />
          ) : (
            <AssistantBubble
              key={index}
              message={message}
              onFollowUp={onSend}
            />
          )
        )}
        {pending ? <PendingBubble /> : null}
        <div ref={bottomRef} aria-hidden="true" />
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-3 pb-3">
        <div className="bg-fs-surface-primary border-fs-border-muted relative rounded-2xl border px-3 pt-3 pb-8">
          <textarea
            ref={textareaRef}
            maxLength={1000}
            placeholder="Ask about this product or brand"
            rows={1}
            value={draft}
            readOnly={!interactive}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submitDraft();
              }
            }}
            className="type-fs-paragraph text-fs-text-primary placeholder:text-fs-text-subdued w-full resize-none overflow-hidden bg-transparent leading-relaxed outline-none"
          />
          <div className="absolute right-2 bottom-2">
            <button
              type="button"
              aria-label="Send message"
              onClick={submitDraft}
              disabled={!interactive || draft.trim().length === 0}
              className="text-fs-text-primary flex size-7 items-center justify-center rounded-full bg-transparent disabled:opacity-60"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerAssistantPanel;

const UserBubble: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex w-full flex-col justify-end">
    <div className="bg-(--slate-color-grey-400) ml-auto flex min-h-8 max-w-full items-center rounded-full px-3 py-1.5">
      <p className="type-fs-paragraph text-fs-text-primary">{text}</p>
    </div>
  </div>
);

const PendingBubble: React.FC = () => (
  <div className="flex w-full max-w-[95%] flex-col gap-2">
    <span className="type-fs-paragraph text-fs-text-subdued animate-pulse">
      Researching…
    </span>
  </div>
);

const AssistantBubble: React.FC<{
  message: AssistantMessage;
  onFollowUp?: (text: string) => void;
}> = ({ message, onFollowUp }) => {
  const [researchOpen, setResearchOpen] = useState(false);

  return (
    <div className="flex w-full max-w-[95%] flex-col gap-2">
      {message.researched ? (
        <button
          type="button"
          aria-expanded={researchOpen}
          onClick={() => setResearchOpen((open) => !open)}
          className="text-fs-text-subdued flex w-fit items-center gap-1"
        >
          <span className="type-fs-paragraph">Researched</span>
          <ChevronDown open={researchOpen} />
        </button>
      ) : null}

      <div className="type-fs-paragraph text-fs-text-primary space-y-2 [&_strong]:font-semibold">
        {message.segments.map((segment, index) =>
          segment.kind === "paragraph" ? (
            <p key={index} className="m-0">
              {segment.content}
            </p>
          ) : (
            <ul key={index} className="m-0 list-outside list-disc pl-6">
              {segment.items.map((item, itemIndex) => (
                <li key={itemIndex} className="py-1">
                  {item}
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {/* Feedback actions */}
      <div className="mt-[-4px] flex items-center gap-1">
        <button
          type="button"
          aria-label="Thumbs up"
          className="hover:bg-fs-action-surface-inverse-hover flex size-8 items-center justify-center rounded-[6px] bg-transparent transition-colors"
        >
          <ThumbsUpIcon />
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          className="hover:bg-fs-action-surface-inverse-hover flex size-8 items-center justify-center rounded-[6px] bg-transparent transition-colors"
        >
          <ThumbsDownIcon />
        </button>
      </div>

      {message.followUps && message.followUps.length > 0 ? (
        <div className="mt-3 flex flex-col gap-3">
          <p className="type-fs-paragraph text-fs-text-subdued">
            Suggested questions
          </p>
          <div className="flex flex-col items-start gap-2">
            {message.followUps.map((label) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                onClick={() => onFollowUp?.(label)}
                className={FOLLOW_UP_PILL_CLASS}
              >
                <span className="block min-w-0 truncate">{label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const FaireMark: React.FC = () => (
  <svg viewBox="0 0 11 11" width="8" height="8" fill="#FFFFFF" aria-hidden="true">
    <path d="M10.75 2.567 9.676 0H.892v.153q.427.06.695.137.27.062.506.306.094.091.142.305.063.2.079.459.032.26.032.55.015.29.015.565V6.34q0 .458-.015.902 0 .428-.016.794v.58q0 .246-.016.505-.016.244-.063.473-.032.229-.08.413a.7.7 0 0 1-.126.26q-.221.244-.568.382a3.2 3.2 0 0 1-.727.198V11h5.055v-.153q-.647-.03-.995-.137-.332-.123-.474-.336-.142-.215-.19-.642A24 24 0 0 1 4.1 8.8q0-.26-.016-.611V5.5h.759q.158 0 .331.015.174 0 .411.016.459.03.79.168a2 2 0 0 1 .648.382q.33.29.568.84.238.535.364 1.023h.19V3.025h-.19q-.063.382-.142.718a1.8 1.8 0 0 1-.269.596q-.19.275-.521.473-.332.2-.9.321-.222.045-.554.077l-.647.03q-.3.015-.553.016h-.285V2.612q.015-.641.032-1.176a17 17 0 0 1 .063-.78q.015-.12.032-.213A1 1 0 0 0 4.258.26h.237a4 4 0 0 1 .363-.016A5 5 0 0 1 5.252.23q.205-.015.364-.015.931 0 1.627.092.71.076.98.198.552.26 1.137.81t1.232 1.329z" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--slate-color-icon-subdued)"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ChevronDown: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn("size-4 transition-transform", open && "rotate-180")}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ThumbsUpIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--slate-color-icon-subdued)"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
    aria-hidden="true"
  >
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    <path d="M7 10v12" />
  </svg>
);

const ThumbsDownIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--slate-color-icon-subdued)"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
    aria-hidden="true"
  >
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    <path d="M17 14V2" />
  </svg>
);

const SendIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
    aria-hidden="true"
  >
    <path d="M12 23.143V.857m-6 6 6-6 6 6" />
  </svg>
);
